import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import useManageDocuments from "../../hooks/patientDashboard/useManageDocuments";
import { toast } from "sonner";
import useProfileStore from "../../store/patient/profileStore";
import { FileText, UploadCloud } from "lucide-react";

const initialFormState = {
  document_type: "",
  document_name: null,
};

const UploadDocumentModal = ({ show, setShow, onUploaded }) => {
  const { profile } = useProfileStore();
  const [formState, setFormState] = useState(initialFormState);

  const { handleUploadDocument } = useManageDocuments();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    setShow(false);
    setFormState(initialFormState);
    setError("");
  };

  const onDrop = (acceptedFiles) => {
    setFormState((prevState) => ({
      ...prevState,
      document_name: acceptedFiles[0],
    }));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".pdf,.doc,.docx,.txt",
    multiple: false,
  });

  const handleChange = (e) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formState.document_name) return toast.error("Please upload document");
    setUploading(true);
    setError("");

    try {
      const payload = {
        file: formState.document_name,
        document_type: formState.document_type,
      };

      await handleUploadDocument(profile?.id, payload);
      toast.success("Document uploaded successfully");

      if (onUploaded) {
        await onUploaded();
      }

      handleClose();
    } catch (uploadError) {
      console.error("Error:", uploadError);
      setError(uploadError?.message || "Unable to upload document right now.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="app-modal app-modal-upload">
      <Modal.Header closeButton>
        <Modal.Title>Upload a Document</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="upload-modal-intro">
            <span className="upload-modal-icon">
              <FileText size={20} />
            </span>
            <div>
              <h6>Add a new file to your document library</h6>
              <p>Choose a category, then drag in the file you want to keep on record.</p>
            </div>
          </div>

          <div className="mb-4">
            <Form.Label>Category</Form.Label>
            <Form.Select
              type="text"
              placeholder="Select Category"
              onChange={handleChange}
              name="document_type"
              value={formState.document_type}
            >
              <option value="">--Select Document Type--</option>
              <option value="case notes">Case Notes</option>
              <option value="admissions">Admission</option>
              <option value="financial">Financial</option>
              <option value="labs">Labs</option>
              <option value="imaging">Imaging</option>
              <option value="procedures">Procedures</option>
              <option value="antenatal">Antenatal</option>
              <option value="prescription">Prescription</option>
              <option value="consent form">Consent Form</option>
              <option value="medical report">Medical Report</option>
              <option value="referral form">Referral Form</option>
            </Form.Select>
          </div>

          <div className="mb-2">
            <Form.Label>File</Form.Label>
            <div
              {...getRootProps()}
              className={`upload-dropzone ${isDragActive ? "is-active" : ""} ${
                formState.document_name ? "has-file" : ""
              }`}
            >
              <input {...getInputProps()} />
              <span className="upload-dropzone-icon">
                <UploadCloud size={18} />
              </span>
              {formState.document_name ? (
                <div>
                  <strong>{formState.document_name.name}</strong>
                  <p>Click to replace this file before uploading.</p>
                </div>
              ) : (
                <div>
                  <strong>Drag and drop a document here</strong>
                  <p>Or click to choose a file from your device.</p>
                </div>
              )}
            </div>
          </div>

          {error ? <div className="upload-modal-error">{error}</div> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button variant="primary" disabled={uploading} type="submit">
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UploadDocumentModal;
